import { useEffect, useRef } from 'react';
import { usePageTracking } from './usePageTracking';
import { useConversionTracking } from './useConversionTracking';
import { useEnhancedAnalytics } from './useEnhancedAnalytics';

interface ElementVisibility {
  elementId: string;
  isVisible: boolean;
  visibilityPercentage: number;
  timeVisible: number;
  firstSeen: number;
}

interface PageInteraction {
  type: 'click' | 'hover' | 'scroll' | 'focus';
  element: string;
  timestamp: number;
  coordinates?: { x: number; y: number };
}

export const useAdvancedPageTracking = (pageName: string) => {
  const baseTracking = usePageTracking(pageName);
  const { trackElementVisibility, trackCTAPerformance } = useConversionTracking();
  const { trackUserInteraction, trackHeatmapData, trackFeatureInterest } = useEnhancedAnalytics({
    enableUserJourneyTracking: true,
    enableConversionOptimization: true
  });

  const visibleElements = useRef<Map<string, ElementVisibility>>(new Map());
  const interactions = useRef<PageInteraction[]>([]);
  const intersectionObserver = useRef<IntersectionObserver | null>(null);
  const clickHeatmapData = useRef<Array<{ x: number; y: number; element: string; timestamp: number }>>([]);

  // Enhanced Element Visibility Tracking
  useEffect(() => {
    intersectionObserver.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const elementId = entry.target.id || entry.target.className || 'unnamed-element';
          const visibilityPercentage = Math.round(entry.intersectionRatio * 100);
          const isVisible = entry.isIntersecting && visibilityPercentage > 50;
          
          const currentVisibility = visibleElements.current.get(elementId);
          
          if (isVisible && !currentVisibility?.isVisible) {
            // Element became visible
            const visibility: ElementVisibility = {
              elementId,
              isVisible: true,
              visibilityPercentage,
              timeVisible: 0,
              firstSeen: Date.now()
            };
            visibleElements.current.set(elementId, visibility);
            
            trackElementVisibility(elementId, true);
            
            // Track specific feature interests
            if (elementId.includes('cta') || elementId.includes('button')) {
              trackCTAPerformance(elementId, 'view');
            }
            
            if (elementId.includes('feature') || elementId.includes('benefit')) {
              trackFeatureInterest(elementId, 'view');
            }
          } else if (!isVisible && currentVisibility?.isVisible) {
            // Element became hidden
            const timeVisible = Date.now() - currentVisibility.firstSeen;
            visibleElements.current.set(elementId, {
              ...currentVisibility,
              isVisible: false,
              timeVisible
            });
            
            trackElementVisibility(elementId, false);
          }
        });
      },
      {
        threshold: [0, 0.25, 0.5, 0.75, 1.0],
        rootMargin: '0px'
      }
    );

    // Observe all trackable elements
    const elementsToTrack = document.querySelectorAll('[data-track], [id], .cta, .feature, .benefit, button, a');
    elementsToTrack.forEach((element) => {
      intersectionObserver.current?.observe(element);
    });

    return () => {
      intersectionObserver.current?.disconnect();
    };
  }, [trackElementVisibility, trackCTAPerformance, trackFeatureInterest]);

  // Enhanced Click Tracking with Heatmap Data
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const elementType = target.tagName.toLowerCase();
      const elementId = target.id || target.className || `${elementType}-${Date.now()}`;
      
      const interaction: PageInteraction = {
        type: 'click',
        element: elementId,
        timestamp: Date.now(),
        coordinates: { x: event.clientX, y: event.clientY }
      };
      
      interactions.current.push(interaction);
      
      // Track user interaction
      trackUserInteraction(elementType, elementId, 'click');
      
      // Track heatmap data
      trackHeatmapData(event.clientX, event.clientY, elementType);
      
      // Store click data for heatmap
      clickHeatmapData.current.push({
        x: event.clientX,
        y: event.clientY,
        element: elementId,
        timestamp: Date.now()
      });
      
      // Track CTA clicks specifically
      if (target.classList.contains('cta') || target.closest('.cta') || 
          elementId.includes('cta') || elementId.includes('get-early-access')) {
        trackCTAPerformance(elementId, 'click', 'user_initiated');
      }
      
      // Track feature interest clicks
      if (target.closest('.feature') || elementId.includes('feature')) {
        trackFeatureInterest(elementId, 'click');
      }
    };

    const handleHover = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const elementType = target.tagName.toLowerCase();
      const elementId = target.id || target.className || `${elementType}-hover`;
      
      // Track feature interest on hover
      if (target.closest('.feature') || elementId.includes('feature')) {
        trackFeatureInterest(elementId, 'hover');
      }
      
      // Track hover interactions for engagement
      trackUserInteraction(elementType, elementId, 'hover');
    };

    document.addEventListener('click', handleClick);
    document.addEventListener('mouseover', handleHover);

    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('mouseover', handleHover);
    };
  }, [trackUserInteraction, trackHeatmapData, trackCTAPerformance, trackFeatureInterest]);

  // Form Field Interaction Tracking
  useEffect(() => {
    const handleFocus = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        const fieldId = target.id || (target as any).name || 'unnamed-field';
        trackUserInteraction('form-field', fieldId, 'focus');
      }
    };

    const handleBlur = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        const fieldId = target.id || (target as any).name || 'unnamed-field';
        trackUserInteraction('form-field', fieldId, 'blur');
      }
    };

    document.addEventListener('focus', handleFocus, true);
    document.addEventListener('blur', handleBlur, true);

    return () => {
      document.removeEventListener('focus', handleFocus, true);
      document.removeEventListener('blur', handleBlur, true);
    };
  }, [trackUserInteraction]);

  // Scroll Depth with Element Context
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollPercent = Math.round(
            (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
          );
          
          // Track which elements are in viewport during scroll
          const visibleElementIds = Array.from(visibleElements.current.entries())
            .filter(([, data]) => data.isVisible)
            .map(([id]) => id);
          
          if (visibleElementIds.length > 0) {
            trackUserInteraction('viewport', `scroll-${scrollPercent}`, `viewing: ${visibleElementIds.join(', ')}`);
          }
          
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [trackUserInteraction]);

  // Performance and Page Load Tracking
  useEffect(() => {
    const trackPageLoad = () => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      
      if (navigation) {
        trackUserInteraction('page-performance', 'load-complete', JSON.stringify({
          domContentLoaded: Math.round(navigation.domContentLoadedEventEnd - navigation.fetchStart),
          loadComplete: Math.round(navigation.loadEventEnd - navigation.fetchStart),
          firstByte: Math.round(navigation.responseStart - navigation.fetchStart),
          domInteractive: Math.round(navigation.domInteractive - navigation.fetchStart)
        }));
      }
    };

    if (document.readyState === 'complete') {
      trackPageLoad();
    } else {
      window.addEventListener('load', trackPageLoad);
      return () => window.removeEventListener('load', trackPageLoad);
    }
  }, [trackUserInteraction]);

  return {
    ...baseTracking,
    getVisibleElements: () => Array.from(visibleElements.current.entries()),
    getInteractions: () => interactions.current,
    getHeatmapData: () => clickHeatmapData.current,
    getEngagementMetrics: () => ({
      totalInteractions: interactions.current.length,
      uniqueElementsInteracted: new Set(interactions.current.map(i => i.element)).size,
      averageTimePerVisibleElement: Array.from(visibleElements.current.values())
        .filter(v => v.timeVisible > 0)
        .reduce((sum, v) => sum + v.timeVisible, 0) / 
        Array.from(visibleElements.current.values()).filter(v => v.timeVisible > 0).length || 0
    })
  };
};